import { Icon20ExclamationMarkOutline } from '@vkontakte/icons';
import { classNames, Spacing } from '@vkontakte/vkui';
import React, { type FC, type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { overquotaNewCommunication } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { HintPosition } from 'reactApp/ui/Hint/Hint.constants';
import stylesLine from 'reactApp/ui/Space/SingleSpace/SingleSpaceProgress/SingleSpaceProgress.css';
import type { TSpaceInfo } from 'reactApp/ui/Space/SingleSpace/SingleSpaceProgress/SingleSpaceProgress.types';
import { SingleSpaceProgressBar } from 'reactApp/ui/Space/SingleSpace/SingleSpaceProgress/SingleSpaceProgressBar';
import { useSpace } from 'reactApp/ui/Space/SingleSpace/SingleSpaceProgress/useSpace';
import { noopVoid } from 'reactApp/utils/helpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './OverquotaSpaceBlock.css';

export const SpaceLabel: FC<{ item: TSpaceInfo }> = ({ item }): ReactElement => {
    const id = item.id,
        isOverquota = item.isOverquota,
        text = item.text,
        hintText = item.hintText,
        onClick = item?.onClick,
        clickable = Boolean(item?.onClick) || noopVoid;

    const isDarkMode = isDarkThemeModeEnabled();

    return (
        <div className={classNames(stylesLine.infoBlock, { [styles.dark]: isDarkMode })}>
            <Hint text={hintText} positionStrategy={HintPosition.relativeToCursor}>
                <div
                    className={classNames(stylesLine.info, {
                        [stylesLine.info_clickable]: clickable,
                    })}
                    onClick={onClick}
                >
                    <div
                        className={classNames(styles.infoLine, stylesLine[`line_${id}`], styles[`line_${id}`], {
                            [stylesLine.lineFilled_overquota]: isOverquota,
                        })}
                    />
                    <div className={stylesLine.label}>{text}</div>
                </div>
            </Hint>
        </div>
    );
};

export const OverquotaSpaceBlock = () => {
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const { isOverquota, spaceList, freeSpaceText, usedPercents } = useSpace();

    if (!overquotaNewCommunication) {
        return (
            <div className={styles.quota}>
                <div className={styles.quotaIcon}>
                    <Icon20ExclamationMarkOutline />
                </div>
                <div className={styles.quotaTitle}>
                    Занято {used?.value} из {userTotalSpace?.value}
                </div>
                <Spacing size={10} />
                <div className={styles.quotaSeparator} />
                <Spacing size={8} />
                <div className={styles.quotaText}>Пространство переполнено на {overquota?.value}</div>
            </div>
        );
    }

    return (
        <div className={styles.quota}>
            <div className={styles.quotaIcon}>
                <Icon20ExclamationMarkOutline />
            </div>
            <div className={styles.quotaTitle}>
                Занято {used?.value} из {userTotalSpace?.value}
            </div>
            <Spacing size={10} />
            <SingleSpaceProgressBar
                height={6}
                spaceList={spaceList}
                rebranding={true}
                isOverquota={isOverquota}
                freeSpaceText={freeSpaceText}
                usedPercents={usedPercents}
            />
            <Spacing size={8} />
            <div className={styles.quotaText}>Пространство переполнено на {overquota?.value}</div>
            <Spacing size={16} />
            <div className={stylesLine.legend}>
                <div className={stylesLine.quotaInfo}>
                    {spaceList.map((item) => (
                        <SpaceLabel key={item.id} item={item} />
                    ))}
                </div>
            </div>
        </div>
    );
};
